// src/components/forms/AvailabilityPicker.jsx
// Selector estructurado de días y rango horario para solicitudes de servicio.
// Guarda el valor como objeto: { dias: [1,2,3,4,5], inicio: "09:00", fin: "18:00" }
// Compatible con provider_availability (day_of_week 0=Dom … 6=Sáb, start_time, end_time).

import React from 'react';
import {
  Box, FormLabel, FormHelperText, TextField, Typography,
} from '@mui/material';

const DIAS = [
  { label: 'Dom', value: 0 },
  { label: 'Lun', value: 1 },
  { label: 'Mar', value: 2 },
  { label: 'Mié', value: 3 },
  { label: 'Jue', value: 4 },
  { label: 'Vie', value: 5 },
  { label: 'Sáb', value: 6 },
];

/**
 * Props:
 *   value    — objeto { dias: number[], inicio: string, fin: string } o null/undefined
 *   onChange — función que recibe el nuevo valor estructurado
 *   label    — string, etiqueta del campo
 *   error    — boolean
 *   required — boolean
 */
export default function AvailabilityPicker({ value, onChange, label, error, required }) {
  const dias   = value?.dias   ?? [];
  const inicio = value?.inicio ?? '';
  const fin    = value?.fin    ?? '';

  const toggleDia = (dv) => {
    const next = dias.includes(dv)
      ? dias.filter(d => d !== dv)
      : [...dias, dv].sort((a, b) => a - b);
    onChange({ dias: next, inicio, fin });
  };

  const handleInicio = (e) => onChange({ dias, inicio: e.target.value, fin });
  const handleFin    = (e) => onChange({ dias, inicio, fin: e.target.value });

  const isEmpty = dias.length === 0 && !inicio && !fin;

  return (
    <Box sx={{ width: '100%' }}>
      <FormLabel
        sx={{
          display: 'block',
          fontSize: '0.8rem',
          color: error ? '#d32f2f' : '#163244',
          fontWeight: 600,
          mb: 1,
        }}
      >
        {label}{required ? ' *' : ''}
      </FormLabel>

      {/* Selector de días */}
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
        {DIAS.map(({ label: dLabel, value: dv }) => {
          const selected = dias.includes(dv);
          return (
            <Box
              key={dv}
              onClick={() => toggleDia(dv)}
              sx={{
                px: 1.25, py: 0.4,
                borderRadius: 20,
                cursor: 'pointer',
                fontSize: '0.82rem',
                fontWeight: selected ? 700 : 400,
                border: `1.5px solid ${selected ? '#163244' : error ? '#d32f2f' : '#cddae4'}`,
                background: selected ? '#163244' : '#fff',
                color: selected ? '#E7A432' : '#5c7a8a',
                transition: 'all 0.15s',
                userSelect: 'none',
                '&:hover': {
                  borderColor: '#163244',
                  background: selected ? '#163244' : '#f0f5f8',
                },
              }}
            >
              {selected ? `✓ ${dLabel}` : dLabel}
            </Box>
          );
        })}
      </Box>

      {/* Rango horario */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
        <TextField
          label="Desde"
          type="time"
          value={inicio}
          onChange={handleInicio}
          size="small"
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 1800 }} // pasos de 30 min
          sx={{ width: 130 }}
          error={error && !!inicio === false && dias.length > 0}
        />
        <Typography sx={{ color: '#8fa3b1', fontSize: '0.85rem' }}>–</Typography>
        <TextField
          label="Hasta"
          type="time"
          value={fin}
          onChange={handleFin}
          size="small"
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 1800 }}
          sx={{ width: 130 }}
          error={error && !!fin === false && dias.length > 0}
        />
      </Box>

      {/* Resumen legible */}
      {!isEmpty && (
        <Typography sx={{ fontSize: '0.72rem', color: '#8fa3b1', mt: 0.75 }}>
          {dias.length > 0
            ? DIAS.filter(d => dias.includes(d.value)).map(d => d.label).join(', ')
            : 'Sin días seleccionados'}
          {inicio || fin
            ? ` · ${inicio || '?'}–${fin || '?'}`
            : ''}
        </Typography>
      )}

      {error && (
        <FormHelperText error sx={{ mt: 0.5 }}>
          Seleccioná al menos un día y un rango horario
        </FormHelperText>
      )}
    </Box>
  );
}
